import { inject, Injectable } from "@angular/core";
import { PortfolioRemote } from "@modules/portfolio/infrastructure/portfolio.remote";
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of } from "rxjs";
import {
	DustPortfolios,
	PortfolioAssetDetails,
	PortfolioOverview,
	PortfolioOverviewAssetGroup
} from "@modules/portfolio/domain/entities/portfolio-overview.model";
import { PortfolioPerformance } from "@modules/portfolio/domain/entities/portfolio-performance.model";
import {
	catchError,
	distinct,
	distinctUntilChanged,
	filter,
	map,
	retry,
	shareReplay,
	skip,
	switchMap,
	take,
	takeWhile,
	throttleTime,
	timeout,
	withLatestFrom
} from "rxjs/operators";
import {
	BEST_SYMBOL,
	FIAT_TYPE,
	NO_DEPOSITS_VALUE,
	PORTFOLIO_API_TIMEOUT_PERIOD,
	PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
	TRADABLES_TYPE
} from "@helpers/constants";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Fiat, FiatWallet } from "@models";
import { Tradable } from "@models/interfaces/tradable";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { CrossSellingRecommendation } from "@modules/portfolio/domain/entities/portfolio-recommendation.model";
import { BpOption } from "@models/ui-components/bp-option";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { portfolioNewTimeFilter, portfolioTimeFilter } from "@modules/portfolio/domain/portfolio.constants";
import {
	PortfolioAssetGroupProjection,
	PortfolioAssetProjection,
	PortfolioData
} from "@modules/portfolio/domain/entities/portfolio-projection.model";
import { Currency } from "@models/interfaces/currency";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { Index } from "@models/asset/index/index.model";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import * as Util from "../util/portfolio.util";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { Timespan } from "@helpers/const/timespan";
import { UserService } from "@main-services/user.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { TranslationService } from "@core-services/translation.service";
import {
	PortfolioOverviewState,
	PortfolioOverviewStateService
} from "@modules/portfolio/+state/portfolio-overview.state.service";
import {
	PortfolioPerformanceState,
	PortfolioPerformanceStateService
} from "@modules/portfolio/+state/portfolio-performance.state.service";
import { PortfolioWidgetViewModel } from "@modules/portfolio/portfolio-widget/portfolio-widget.component";
import { FeatureHighlights, FeatureHighlightService } from "@core-services/feature-highlight.service";
import { MarginTradingRemote } from "@modules/margin-trading/services/margin-trading.remote";
import { MarginPositionSort, SortOrder } from "../../../api/graphql/bff/generated/types.graphql-gen";
import { PortfolioMarginPositionsPageFragment } from "../../../api/graphql/bff/generated/fragments.service.graphql-gen";
import { BFFAsset, BFFAssetsService } from "@core-services/bff-assets.service";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";

@Injectable({
	providedIn: "root"
})
export class PortfolioFacade {
	public constructor(
		private readonly portfolioRemote: PortfolioRemote,
		private readonly tradableListService: TradableListService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly fiatWalletService: FiatWalletService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly translationService: TranslationService,
		private readonly accountService: AccountService,
		private readonly userService: UserService,
		private readonly profileFacade: NewProfileFacade,
		private readonly fiatService: FiatService,
		private readonly cashplusFacade: CashPlusFacade,
		private readonly stakingFacade: StakingFacade,
		private readonly portfolioOverviewStateService: PortfolioOverviewStateService,
		private readonly portfolioPerformanceStateService: PortfolioPerformanceStateService,
		private readonly featureHighlightService: FeatureHighlightService
	) {}

	// FF Section
	private areNewTimeFiltersActive$ = this.launchDarklyService.isActive$(LD_FLAG.PORTFOLIO_NEW_TIME_FILTERS);
	public hideReturnKpis$ = this.launchDarklyService.isActive$(LD_FLAG.PORTFOLIO_HIDE_BCI_KPIS);
	public showEmptyStateV3$ = this.launchDarklyService.isActive$(LD_FLAG.PORTFOLIO_EMPTY_STATE_V3);
	public showPortfolioCrossSellingSection$ = this.launchDarklyService.isActive$(
		LD_FLAG.PORTFOLIO_CROSS_SELLING_SECTION
	);
	private isGroupedPerformanceActive$: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.PORTFOLIO_GROUPED_PERFORMANCE
	);
	public shouldHighlightAllocation$ = this.featureHighlightService.getShouldHighlight$(
		FeatureHighlights.PORTFOLIO_ALLOCATION
	);
	public isDustToBestEnabled$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.DUST_TO_BEST);

	private hasDustSubject = new BehaviorSubject<boolean>(false);
	public hasDust$ = this.hasDustSubject.asObservable();

	public hideSource = new BehaviorSubject<boolean>(false);
	public isPortfolioDataHidden$: Observable<boolean> = this.hideSource.asObservable();

	public refreshPortfolio = new BehaviorSubject<boolean>(false);
	public isPortfolioRefreshed$: Observable<boolean> = this.refreshPortfolio.asObservable();

	public marginTradingPositions$$ = new BehaviorSubject<void>(null);

	private marginTradingRemote = inject(MarginTradingRemote);
	private bff = inject(BFFAssetsService);

	// Functions to get the needed data to Load Correctly Portfolio

	private getUserHeldAssets(list: Tradable[]): Tradable[] {
		const wallets = this.tradableWalletListService.getList().getValue();
		const tradableIds = wallets.filter((w) => !w.isNew()).map((w) => w.getCurrencyId());
		return list.filter((t) => tradableIds.includes(t.getId()));
	}

	public getDefaultFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public isUnverified(): boolean {
		return this.userService.user.isUnverified();
	}

	public isVerified(): boolean {
		return this.userService.user.isVerified();
	}

	public getTradableWalletHelper(id: string): TradableWallet {
		return this.tradableWalletListService.getWalletsByTradableId(id)[0];
	}

	// Combine Fetches for OVERVIEW and PERFORMANCE data from the API & Update States

	public fetchPortfolioOverviewAndPerformance(timespan?): void {
		this.fetchPortfolioPerformance(timespan);
		this.fetchPortfolioOverview();
	}

	// Fetch the Portfolio OVERVIEW data from the API & Update State

	public getPortfolioOverviewState$(): Observable<PortfolioOverviewState> {
		return this.portfolioOverviewStateService.getState$().pipe(filter((state) => !!state));
	}

	private fetchPortfolioOverview(): void {
		// We don't want to show the LoadingState when receive socket data, only on Load
		if (!this.portfolioOverviewStateService.isOverviewLoaded() || this.portfolioOverviewStateService.hasError()) {
			this.portfolioOverviewStateService.setIsLoading(true);
		}

		this.portfolioRemote
			.getPortfolioOverview()
			.pipe(timeout(PORTFOLIO_API_TIMEOUT_PERIOD), retry({ count: 5, delay: 1000 }))
			.subscribe({
				next: (overview) => {
					this.portfolioOverviewStateService.updateState({ ...overview, error: false, isLoading: false });
				},
				error: () => {
					this.portfolioOverviewStateService.overviewErrorState();
				}
			});
	}

	// Fetch the Portfolio PERFORMANCE data from the API & Update State

	public getPortfolioPerformanceState$(): Observable<PortfolioPerformanceState> {
		return this.portfolioPerformanceStateService.getState$().pipe(filter((state) => !!state));
	}

	private fetchPortfolioPerformance(timespan?): void {
		// We don't want to show the LoadingState when receive socket data, only on Load
		if (
			!this.portfolioPerformanceStateService.isPerformanceLoaded() ||
			this.portfolioPerformanceStateService.hasError()
		) {
			this.portfolioPerformanceStateService.setIsLoading(true);
		}

		this.portfolioRemote
			.getPortfolioPerformance(timespan || Timespan.Day)
			.pipe(timeout(PORTFOLIO_API_TIMEOUT_PERIOD), retry({ count: 5, delay: 1000 }))
			.subscribe({
				next: (performance) => {
					this.portfolioPerformanceStateService.updateState({
						...performance,
						error: false,
						isLoading: false
					});
				},
				error: () => {
					this.portfolioPerformanceStateService.performanceErrorState();
				}
			});
	}

	// Get Portfolio Widget Data

	private getPortfolioWidgetData$(): Observable<PortfolioData> {
		return combineLatest({
			overview: this.getPortfolioOverviewState$(),
			performance: this.getPortfolioPerformanceState$(),
			profile: this.profileFacade.getProfile$(),
			language: this.translationService.languageUpdate$(),
			timespan: this.getPortfolioChartOptions$(),
			isPortfolioHidden: this.isPortfolioDataHidden$
		}).pipe(
			withLatestFrom(this.isGroupedPerformanceActive$),
			map(([data, isGroupedActive]) => {
				let { overview, performance, profile, language, timespan, isPortfolioHidden } = data;

				if (!overview.portfolios || !performance.overall_portfolio_current_value) {
					return Util.getEmptyPortfolioWidgetAndPageData(overview, performance, isGroupedActive, profile);
				}

				if (isGroupedActive) {
					performance = Util.sortAssets(performance, this.getDefaultFiat());
				}

				const assetsGroup = this.getAssetGroupsPortfolio(overview, performance, isGroupedActive);
				const assets = Util.getAssetsWidgetPortfolio(assetsGroup);

				return Util.returnPortfolioWidgetData(
					overview,
					performance,
					isGroupedActive,
					assetsGroup,
					profile,
					language,
					timespan,
					assets,
					isPortfolioHidden
				);
			})
		);
	}

	public getPortfolioWidgetValues$(): Observable<PortfolioWidgetViewModel> {
		return this.getPortfolioWidgetData$().pipe(
			map((data) => ({
				assets: data.assets,
				portfolioData: data,
				fiatSum: +(data.projection.groups.find((val) => val.isFiat)?.fiatBalance ?? "0"),
				assetCount: data.assets?.length,
				isLoading: data.isLoading,
				apiError: data.apiError,
				profile: data.profileVM,
				language: data.languageSelected,
				timespanOptions: data.timespanOptions,
				isHidePortfolioValueActive: data.isHidePortfolioValueActive
			}))
		);
	}

	// Get Portfolio Page Data

	private _portfolioPageData$: Observable<PortfolioData> | null = null;

	public getPortfolioPageData$(): Observable<PortfolioData> {
		if (this._portfolioPageData$) {
			return this._portfolioPageData$;
		}
		this._portfolioPageData$ = combineLatest([
			this.getPortfolioOverviewState$().pipe(
				distinctUntilChanged(
					(a, b) =>
						JSON.stringify(a.portfolios?.map((p) => p.asset_pid)) ===
						JSON.stringify(b.portfolios?.map((p) => p.asset_pid))
				),
				switchMap(this.fetchBFFAssetHandler)
			),
			this.getPortfolioPerformanceState$(),
			this.stakingFacade.getStakeables$().pipe(filter((stk) => !!stk)),
			this.cashplusFacade.getCombinedCashPlusState$(),
			this.translationService.languageUpdate$(),
			this.getPortfolioChartOptions$(),
			this.hideReturnKpis$,
			this.showPortfolioCrossSellingSection$,
			this.shouldHighlightAllocation$,
			this.isPortfolioDataHidden$,
			this.isDustToBestEnabled$,
			this.getAssetDusts$(),
			this.hasAssetDust$(),
			this.getMarginPositions$()
		]).pipe(
			withLatestFrom(this.isGroupedPerformanceActive$),
			map(([data, isGroupedActive]) => {
				let [
					overview,
					performance,
					stakeableAssets,
					cashPlus,
					language,
					timespan,
					isKpisEnabled,
					showCrossSellingSection,
					shouldHighlightAllocation,
					hidePortfolioValue,
					isDustToBestEnabled,
					assetDust,
					hasDust,
					marginTradingPositions
				] = data;

				if (!overview.portfolios || !performance.overall_portfolio_current_value) {
					return Util.getEmptyPortfolioWidgetAndPageData(overview, performance, isGroupedActive);
				}

				const stakeableAssetsEnabled = stakeableAssets.filter(
					(asset) => asset.availability !== StakeableAvailability.DISABLED
				);

				if (isGroupedActive) {
					performance = Util.sortAssets(performance, this.getDefaultFiat());
				}

				const assetGroups = this.getAssetGroupsPortfolio(
					overview,
					performance,
					isGroupedActive,
					stakeableAssetsEnabled
				);

				const dustAssetGroupType = assetDust.map((asset) => asset.asset_group);

				return Util.returnPortfolioPageData(
					overview,
					performance,
					isGroupedActive,
					assetGroups,
					cashPlus,
					language,
					timespan,
					isKpisEnabled,
					showCrossSellingSection,
					shouldHighlightAllocation,
					hidePortfolioValue,
					isDustToBestEnabled,
					assetDust,
					dustAssetGroupType,
					hasDust,
					marginTradingPositions
				);
			}),
			shareReplay(1)
		);
		return this._portfolioPageData$;
	}

	private fetchBFFAssetHandler = (
		portfolioAssetDetailList: PortfolioOverviewState
	): Observable<PortfolioOverviewState> => {
		if (portfolioAssetDetailList?.portfolios?.length > 0) {
			const unfullfilled = portfolioAssetDetailList.portfolios.filter(
				(f) => !this.tradableListService.getItemByPid(f.asset_pid) && f.asset_group.toUpperCase() !== FIAT_TYPE
			);
			const s$ = unfullfilled.map((asset) =>
				this.bff.getAssetFetch({ input: { pid: asset.asset_pid } }).pipe(take(1))
			);
			if (s$.length > 0) {
				return forkJoin(s$).pipe(
					map((assets: BFFAsset[]) => {
						const tradableAssets = assets.map((a) => new BFFSecurityAsset(a));
						this.tradableListService.addItems(tradableAssets);
						return portfolioAssetDetailList;
					})
				);
			}
			return of(portfolioAssetDetailList);
		}
		return of(portfolioAssetDetailList);
	};

	// Functions to simplify the code for the Portfolio Widget & Page Data

	private getMarginPositions$(): Observable<PortfolioMarginPositionsPageFragment> {
		return this.marginTradingPositions$$.pipe(
			switchMap((_) => {
				return this.marginTradingRemote.getMarginPositions({
					marginPositionsInput: {
						sortBy: MarginPositionSort.MarginLevel,
						sortOrder: SortOrder.Desc
					}
				});
			})
		);
	}

	private getAssetGroupsPortfolio(
		overview: PortfolioOverviewState,
		performance: PortfolioPerformanceState,
		isGroupedActive: boolean,
		isStakeableAsset?: Stakeable[]
	): PortfolioAssetGroupProjection[] {
		return Util.groupAssetsPerGroup(overview)
			.map((group) =>
				this.mapAssetGroupProjection(group, overview, performance, isGroupedActive, isStakeableAsset)
			)
			.sort((a, b) => (+a.fiatBalance > +b.fiatBalance ? 1 : -1))
			.sort((a, _) => (a.assetGroup.toUpperCase() === this.getDefaultFiat().getCurrencyType() ? 1 : -1));
	}

	//Get Prices Updates from Tradable & Fiat Wallets

	// Emits on wallet and tradable updates
	public getPriceUpdates$(): Observable<[TradableWallet[], FiatWallet[]] | Tradable[]> {
		// Update in case of new tradable wallets
		const tradableWalletUpdates$ = this.tradableWalletListService
			.getList()
			.pipe(takeWhile((list) => list.length !== 0));

		// Update in case of new fiat wallets
		const fiatWalletsUpdates$ = this.fiatWalletService.getList().pipe(takeWhile((list) => list.length !== 0));

		// Only emit when both are loaded
		const walletUpdates$ = combineLatest([tradableWalletUpdates$, fiatWalletsUpdates$]).pipe(
			throttleTime(10000), // Fiat wallets emit many times in a row...
			skip(1)
		);

		// Update in case of new prices
		const tradableUpdates$ = this.tradableListService.getList().pipe(
			skip(1), // Skip initial emit
			map((list) => this.getUserHeldAssets(list)), // Select only user held assets
			filter((list) => list?.length > 0), // Don't emit if user holds no assets
			distinct((list) => JSON.stringify(list.map((t) => t.getPrice()))), // Only emit if any price actually changes
			throttleTime(5000) // No need to update multiple times if we get multiple emits in a row
		);

		return merge(walletUpdates$, tradableUpdates$);
	}

	// Map Asset Group Projection and Map Asset Projection

	public mapAssetGroupProjection(
		group: PortfolioOverviewAssetGroup,
		overview: PortfolioOverview,
		performance: PortfolioPerformance,
		isGroupedPerformanceActive?: boolean,
		stakeableAssets?: Stakeable[]
	): PortfolioAssetGroupProjection {
		const isFiat = group.asset_group === FIAT_TYPE;
		let mappedAssets: PortfolioAssetProjection[];

		if (stakeableAssets) {
			mappedAssets = group.assets
				.map((asset) => {
					let stakeableAsset = stakeableAssets.find((val) => val.id === asset.asset_id);
					const isSoldOut = stakeableAsset?.availability === StakeableAvailability.SOLD_OUT ? true : false;
					const isStakeable = stakeableAsset !== undefined;
					const interestRate = stakeableAsset?.yearlyInterest ?? 0;
					return this.mapAssetProjection(isFiat, asset, isStakeable, isSoldOut, interestRate, stakeableAsset);
				})
				// this filters out negative balances
				.filter((asset) => +asset.assetBalance >= 0)
				// this filters out any fiats where we don't have a proper asset to render (for example BGN, that now is deactivated)
				.filter((asset) => !isFiat || (isFiat && asset.id !== undefined))
				.sort((a, b) => (+a.fiatBalance < +b.fiatBalance ? 1 : -1));
		} else {
			mappedAssets = group.assets
				.map((asset) => this.mapAssetProjection(isFiat, asset))
				.filter((asset) => +asset.assetBalance >= 0)
				.sort((a, b) => (+a.fiatBalance < +b.fiatBalance ? 1 : -1));
		}

		let returnPercentage;
		let fiatBalance;
		if (isGroupedPerformanceActive) {
			let performanceGroup = performance.asset_group_performances.find(
				(assetGroupPerformance) => assetGroupPerformance.asset_group === group.asset_group
			);
			returnPercentage = performanceGroup?.return_percentage;
			fiatBalance = performanceGroup?.fiat_balance;
		} else {
			let overviewGroup = overview.asset_group_total_returns.find(
				(overviewAssetGroup) => overviewAssetGroup.asset_group === group.asset_group
			);
			returnPercentage = overviewGroup?.total_return_percent;
			fiatBalance = overviewGroup?.total_fiat_balance;
		}

		let logo = this.getDefaultFiat().getLogo();
		if (!isFiat) {
			logo = "/assets/images/portfolio/" + Util.getAssetGroupName(group.asset_group) + "-group.svg";
		}

		return {
			assetGroup: Util.getAssetGroupName(group.asset_group),
			name: Util.getAssetGroupName(group.asset_group) + "wallet_section_header",
			assets: mappedAssets,
			totalReturnPercentage: returnPercentage,
			// this is a hack to show the fiat tile even if backend returns currently undefined when a user has all their fiat in Cash Plus
			fiatBalance: fiatBalance ? fiatBalance : 0,
			logo: logo,
			isTotalReturn: !isGroupedPerformanceActive,
			isFiat: isFiat,
			hasBalance:
				(!!fiatBalance && +fiatBalance !== 0) ||
				Util.getAssetGroupName(group.asset_group).toUpperCase() === "FIAT"
		};
	}

	private mapAssetProjection(
		isFiat: boolean,
		asset: PortfolioAssetDetails,
		isStakeable: boolean = false,
		isSoldOut: boolean = false,
		interestRate: number = 0,
		stakeable?: Stakeable
	): PortfolioAssetProjection {
		let currency: Currency;
		if (isFiat) {
			currency = this.fiatService.list.find((fiat: Currency) => Util.isSameCurrency(asset, fiat));
			isStakeable = false;
		} else {
			currency = this.tradableListService.getItemByPid(asset.asset_pid);
		}

		return {
			id: currency?.getId(),
			pid: asset.asset_pid, // I don't like this. Why so complicated??
			logo: currency?.getLogo(),
			name: currency?.getName(),
			symbol: currency?.getSymbol(),
			assetBalance: asset.asset_balance,
			fiatBalance: asset.fiat_balance,
			totalReturnPercent: asset.total_return_percent,
			isAllTimeReturn: false,
			isFiat: isFiat,
			isIndex: currency instanceof Index,
			showFiatConversion: currency?.getId() !== this.getDefaultFiat().getId(),
			parkedAssetBalance: asset.parked_asset_balance,
			availableAssetBalance: asset.available_asset_balance,
			defaultFiat: this.getDefaultFiat(),
			stakingInformation: {
				stakedBalancePercent: +asset.staked_balance_percent,
				isSoldOut: isSoldOut,
				isStakeable: isStakeable,
				yearlyInterestRate: interestRate,
				stakeable: stakeable
			},
			isLegacyDigiAsset:
				currency?.getCurrencyType() === TRADABLES_TYPE.STOCK ||
				currency?.getCurrencyType() === TRADABLES_TYPE.ETC ||
				currency?.getCurrencyType() === TRADABLES_TYPE.ETF,
			isRealSecurity: currency instanceof BFFSecurityAsset
		};
	}

	// Other functions

	public getCrossSellingRecommendations$(): Observable<CrossSellingRecommendation> {
		return this.portfolioRemote.getCrossSellingSuggestions().pipe(
			map((performance) => {
				if (!performance) {
					return { recommended_asset_groups: [] };
				}
				return performance;
			})
		);
	}

	public getPortfolioChartOptions$(): Observable<BpOption<string, Timespan>[]> {
		return this.areNewTimeFiltersActive$.pipe(
			map((areActive) => {
				if (areActive) {
					return portfolioNewTimeFilter;
				}
				return portfolioTimeFilter;
			})
		);
	}

	public hasDeposits$(): Observable<boolean> {
		return this.getPortfolioPerformanceState$().pipe(
			map((portfolioPerformance) => +portfolioPerformance.overall_portfolio_current_value > NO_DEPOSITS_VALUE),
			catchError(() => of(false))
		);
	}

	public isZeroBalance$(): Observable<boolean> {
		return this.getPortfolioPerformanceState$().pipe(
			map((state) => state.overall_portfolio_current_value === PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS)
		);
	}

	public changeHideStatus(status: boolean): void {
		this.hideSource.next(status);
		sessionStorage.setItem("isHideActive", JSON.stringify(status));
	}

	public changeRefreshPortfolioStatus(status: boolean): void {
		this.refreshPortfolio.next(status);
	}

	// Dust to BEST
	public getAssetDusts$(): Observable<DustPortfolios[]> {
		return combineLatest({
			tradableList: this.tradableListService.getList(),
			overview: this.portfolioOverviewStateService.portfolios$
		}).pipe(
			filter(({ tradableList, overview }) => !!tradableList && !!overview),
			take(1),
			map(({ tradableList, overview }) => {
				const dustWallets = Util.filterDustWallets(overview, tradableList, this.getDefaultFiat());

				// Update the status of dust to show or hide entry point button
				dustWallets.length > 0 ? this.hasDustSubject.next(true) : this.hasDustSubject.next(false);

				// Sort dust by fiat_balance in descending order to get the 10 highest ones
				dustWallets.sort((a, b) => +b.fiat_balance - +a.fiat_balance);

				return Util.deserializeDustWallet(
					dustWallets,
					this.getDefaultFiat(),
					this.tradableListService.findBySymbolOrName(BEST_SYMBOL).getAssetPrices().getPrice()
				);
			})
		);
	}

	public hasAssetDust$(): Observable<boolean> {
		return combineLatest({
			tradableList: this.tradableListService.getList(),
			overview: this.portfolioOverviewStateService.portfolios$
		}).pipe(
			filter(({ tradableList, overview }) => !!tradableList && !!overview),
			map(({ tradableList, overview }) => {
				const dustWallets = Util.filterDustWallets(overview, tradableList, this.getDefaultFiat());
				return dustWallets.length > 0;
			})
		);
	}
}
